import React, { useState } from 'react';
import {
  Switch,
  FormControlLabel,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { CheckCircle, Cancel, Warning } from '@mui/icons-material';
import axios from 'axios';

const MaidStatusToggle = ({ 
  maidProfile, 
  onStatusChange, 
  showLabel = true, 
  variant = 'switch', // 'switch' | 'chip' | 'button'
  confirmAction = true 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  const isActive = maidProfile?.user?.isActive ?? false;

  const handleStatusChange = async (newStatus) => {
    if (confirmAction) {
      setPendingStatus(newStatus);
      setConfirmDialog(true);
      return;
    }
    
    await executeStatusChange(newStatus);
  };

  const executeStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      setError('');
      setConfirmDialog(false);

      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

      const response = await axios.patch(
        `${apiBase}/api/maids/${maidProfile.id}/status`,
        { isActive: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        onStatusChange?.(maidProfile.id, newStatus);
      } else {
        setError(response.data.error || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating maid status:', err);
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setLoading(false);
      setPendingStatus(null);
    }
  };

  const handleConfirmAction = () => {
    executeStatusChange(pendingStatus);
  };

  const handleCancelAction = () => {
    setConfirmDialog(false);
    setPendingStatus(null);
  };

  // Confirm Dialog Component
  const ConfirmDialog = () => (
    <Dialog open={confirmDialog} onClose={handleCancelAction} maxWidth="sm">
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning color="warning" />
          <Typography variant="h6">
            Confirm Status Change
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to {pendingStatus ? 'activate' : 'deactivate'} this technician?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <strong>{maidProfile?.user?.firstName} {maidProfile?.user?.lastName}</strong>
          {pendingStatus
            ? ' will be able to receive new bookings and appear in search results.'
            : ' will not receive new bookings and will be hidden from search results.'
          }
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelAction}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmAction}
          variant="contained"
          color={pendingStatus ? 'success' : 'error'}
          disabled={loading}
        >
          {loading ? 'Updating...' : (pendingStatus ? 'Activate' : 'Deactivate')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (variant === 'chip') {
    return (
      <Box>
        <Chip
          icon={
            loading ? (
              <CircularProgress size={16} />
            ) : isActive ? (
              <CheckCircle />
            ) : (
              <Cancel />
            )
          }
          label={isActive ? 'Active' : 'Inactive'}
          color={isActive ? 'success' : 'default'}
          onClick={() => handleStatusChange(!isActive)}
          disabled={loading}
          clickable
          sx={{ cursor: 'pointer' }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 1, fontSize: '0.875rem' }}>
            {error}
          </Alert>
        )}
        <ConfirmDialog />
      </Box>
    );
  }

  if (variant === 'button') {
    return (
      <Box>
        <Button
          variant={isActive ? 'contained' : 'outlined'}
          color={isActive ? 'success' : 'error'}
          startIcon={
            loading ? (
              <CircularProgress size={16} />
            ) : isActive ? (
              <CheckCircle />
            ) : (
              <Cancel />
            )
          }
          onClick={() => handleStatusChange(!isActive)}
          disabled={loading}
          size="small"
        >
          {isActive ? 'Deactivate' : 'Activate'}
        </Button>
        {error && (
          <Alert severity="error" sx={{ mt: 1, fontSize: '0.875rem' }}>
            {error}
          </Alert>
        )}
        <ConfirmDialog />
      </Box>
    );
  }

  // Default switch variant
  const SwitchComponent = () => (
    <Switch
      checked={isActive}
      onChange={(e) => handleStatusChange(e.target.checked)}
      disabled={loading}
      color="success"
    />
  );

  return (
    <Box>
      {showLabel ? (
        <FormControlLabel
          control={
            loading ? (
              <Box display="flex" alignItems="center" justifyContent="center" width={58} height={38}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <SwitchComponent />
            )
          }
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>Status</Typography>
              <Chip
                size="small"
                label={isActive ? 'Active' : 'Inactive'}
                color={isActive ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>
          }
        />
      ) : (
        loading ? (
          <CircularProgress size={20} />
        ) : (
          <SwitchComponent />
        )
      )}
      
      {error && (
        <Alert severity="error" sx={{ mt: 1, fontSize: '0.875rem' }}>
          {error}
        </Alert>
      )}
      
      <ConfirmDialog />
    </Box>
  );
};

export default MaidStatusToggle;